/**
 * Shared paginated timeline rendering used by both BookingTimeline
 * (inside BookingDetailDialog) and ActivityPanel (inside
 * UserExplorerView's UserDetailView). Both consume the same
 * TimelineResponse shape and render near-identical event rows.
 *
 * The hook handles initial fetch + cursor-based load-more + error
 * recovery; the component renders skeleton/empty/error states and
 * the event rows themselves.
 */
import { useCallback, useEffect, useState } from 'react'
import { iconFor } from './timelineIcons'
import { timeAgo } from '../../lib/format'
import type { TimelineCursor, TimelineEvent, TimelineResponse } from '../../lib/admin-api'

type FetchPage = (cursor: TimelineCursor | null) => Promise<TimelineResponse>

interface UseTimelineEventsResult {
  events: TimelineEvent[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  nextCursor: TimelineCursor | null
  loadMore: () => Promise<void>
}

export function useTimelineEvents(fetchPage: FetchPage, deps: React.DependencyList): UseTimelineEventsResult {
  const [events, setEvents] = useState<TimelineEvent[]>([])
  const [nextCursor, setNextCursor] = useState<TimelineCursor | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setEvents([])
    setNextCursor(null)

    fetchPage(null)
      .then((data) => {
        if (cancelled) return
        setEvents(data.events)
        setNextCursor(data.next_cursor)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load timeline')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // fetchPage changes on every render; depend on the identifier list
    // the caller provides instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    setError(null)
    try {
      const data = await fetchPage(nextCursor)
      setEvents((prev) => [...prev, ...data.events])
      setNextCursor(data.next_cursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more events')
    } finally {
      setLoadingMore(false)
    }
  }, [fetchPage, nextCursor, loadingMore])

  return { events, loading, loadingMore, error, nextCursor, loadMore }
}

interface TimelineListProps {
  timeline: UseTimelineEventsResult
  /** Skeleton row count while first page loads. */
  skeletonRows?: number
  /** Node rendered when events is empty and not loading/errored. */
  emptyState: React.ReactNode
  ariaLabel: string
}

export function TimelineList({
  timeline,
  skeletonRows = 3,
  emptyState,
  ariaLabel,
}: TimelineListProps) {
  const { events, loading, loadingMore, error, nextCursor, loadMore } = timeline

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: skeletonRows }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-stone-200 dark:bg-stone-800 animate-pulse" />
            <div className="flex-1 h-4 bg-stone-100 dark:bg-stone-800 rounded animate-pulse" />
          </div>
        ))}
      </div>
    )
  }

  if (error && events.length === 0) {
    return <p className="text-sm text-red-600 dark:text-red-400">Could not load timeline: {error}</p>
  }

  if (events.length === 0) {
    return <>{emptyState}</>
  }

  return (
    <div className="space-y-1" role="log" aria-label={ariaLabel}>
      {events.map((event) => {
        const { icon, color } = iconFor(event.event_type)
        return (
          <div key={event.source_id} className="flex items-start gap-2.5 py-1">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-mono font-bold ${color}`}
            >
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-stone-700 dark:text-stone-300 leading-tight">{event.detail}</p>
            </div>
            <span className="text-xs font-mono text-stone-400 dark:text-stone-500 flex-shrink-0 tabular-nums">
              {timeAgo(event.timestamp)}
            </span>
          </div>
        )
      })}
      {nextCursor && (
        <div className="pt-2">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="text-xs font-medium text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
      {error && events.length > 0 && (
        <p className="text-xs text-red-600 dark:text-red-400 pt-1">Load failed: {error}</p>
      )}
    </div>
  )
}
